import { useRef } from 'react';
import { useQuery } from 'react-query';
import { getJobAsync } from '../../index';
import { IJobProps, JobStatus } from '../../types';

export const JOB_KEYS = {
  ALL: 'jobs',
};

const TERMINAL_STATUSES: JobStatus[] = ['Completed', 'Failed', 'Canceled'];
const POLL_INTERVAL_MS = 1500;
// Após um status terminal, se algum dispositivo ainda estiver "processing" (chamada HTTP que já
// estava em voo no momento do cancelamento, não dava pra abortar), continua dando poll por até esse
// tempo pra pegar o resultado real assim que ele chegar — sem isso a tela ficaria congelada com o
// último estado conhecido antes de o job virar terminal.
const CANCEL_GRACE_MS = 8000;

const hasProcessingDevice = (json?: string | null): boolean => {
  if (!json) return false;
  try {
    const parsed = JSON.parse(json) as Array<{ Status?: string }>;
    return parsed.some((d) => d.Status === 'processing');
  } catch {
    return false;
  }
};

export const useJobQuery = (jobId: number | null) => {
  const graceDeadlineRef = useRef<number | null>(null);

  return useQuery<IJobProps, Error>(
    [JOB_KEYS.ALL, jobId],
    () => getJobAsync(jobId as number),
    {
      enabled: jobId !== null,
      refetchOnWindowFocus: false,
      refetchInterval: (data) => {
        if (!data) return POLL_INTERVAL_MS;
        if (!TERMINAL_STATUSES.includes(data.status)) {
          // Job ativo (ou um novo job no mesmo componente): zera o deadline pra que um
          // cancelamento futuro ganhe seu próprio grace period, não o expirado do anterior.
          graceDeadlineRef.current = null;
          return POLL_INTERVAL_MS;
        }

        if (!hasProcessingDevice(data.logData ?? data.resultData)) return false;

        if (graceDeadlineRef.current === null)
          graceDeadlineRef.current = Date.now() + CANCEL_GRACE_MS;
        if (Date.now() > graceDeadlineRef.current) return false;

        return POLL_INTERVAL_MS;
      },
    },
  );
};
